// useSessionWatchdog
// -------------------
// Periodically probes Cognito for a still-valid session and, on
// failure, signs out + redirects to the login page. Covers the case
// where a user leaves a tab open overnight and the refresh token
// expires — without this, the next API call would just return a
// silent auth error and the UI would sit in a half-working state.
//
// Three triggers:
//   1. On mount — catches sessions that died while the tab was closed.
//   2. On a recurring interval (default 60s) — catches mid-session
//      expiry (refresh-token TTL exceeded, server-side revocation,
//      tenant disabled by support, etc.).
//   3. On document visibilitychange → "visible" — the moment most
//      likely to surface an expired session (user returns to a tab
//      after hours away). Cheaper signal than polling more often.
//
// Auth.currentSession() auto-refreshes the JWT when the refresh
// token is still valid, so most polls are local-only. Only when
// refresh is impossible does it throw — at which point we log out.
//
// Gated first by isAuthPage: the whole auth flow (login, welcome, activate,
// password reset/change) skips polling entirely — there is no session to watch,
// and the /activate challenge reports isAuthenticated() === true WITHOUT a
// session, which would otherwise trip a false logout. cognitologin
// .isAuthenticated() is the secondary gate for non-auth pages (e.g. a
// logged-out state on a dashboard route).

import { useEffect, useRef } from "react";
import { Auth } from "aws-amplify";
import CognitoLoginClass from "@/models/cognitoLogin";
import { LOGIN_PAGE } from "@/constants/definitions";

// 60s strikes the balance: refresh-token expiry is detected within a
// minute of next interaction, but Auth.currentSession() is local for
// the vast majority of calls (only hits Cognito on refresh-token
// rotation, ~once an hour). Bump higher if telemetry shows noise;
// lower if customers report stale-session glitches lasting >60s.
const POLL_INTERVAL_MS = 60_000;

interface UseSessionWatchdogOptions {
  cognitologin: CognitoLoginClass;
  navigate: (path: string) => void;
  // True while the user is on an auth-flow page (login, welcome, activate,
  // password reset/change). These pages have no established session to monitor.
  // Critically, during the NEW_PASSWORD_REQUIRED activation challenge
  // cognitologin.isAuthenticated() is deliberately true — so PrivateRoute admits
  // /activate — even though Auth.currentSession() has nothing to return yet.
  // Probing there would throw and sign the half-activated user out mid-flow, so
  // we skip entirely on auth pages.
  isAuthPage: boolean;
  // Optional override for tests / debugging.
  pollIntervalMs?: number;
}

export function useSessionWatchdog({
  cognitologin,
  navigate,
  isAuthPage,
  pollIntervalMs = POLL_INTERVAL_MS,
}: UseSessionWatchdogOptions): void {
  // Guards against concurrent / re-entrant checks. visibilitychange
  // can fire while a timer-driven probe is in flight; both would call
  // logout() on failure and trigger a double-navigation.
  const checkInFlightRef = useRef<boolean>(false);
  // Keep the latest auth-page status readable from the long-lived interval /
  // visibilitychange callbacks WITHOUT re-subscribing them on every route
  // change (which would reset the poll cadence on each in-app navigation).
  const isAuthPageRef = useRef<boolean>(isAuthPage);
  isAuthPageRef.current = isAuthPage;

  useEffect(() => {
    const probeSession = async (): Promise<void> => {
      // Skip on auth-flow pages: there is no session to watch, and the
      // activation challenge reports isAuthenticated() === true without a real
      // session — probing would trip a false logout mid-activation.
      if (isAuthPageRef.current) return;
      if (!cognitologin.isAuthenticated()) return;
      if (checkInFlightRef.current) return;
      checkInFlightRef.current = true;
      try {
        const session = await Auth.currentSession();
        // Even when Auth.currentSession resolves, a session whose
        // tokens are individually expired can come back invalid —
        // check explicitly. isValid() returns false when the ID
        // token's exp claim is past, which covers the case where
        // local-only retrieval succeeded but the token has aged out.
        if (!session.isValid()) {
          throw new Error("Cognito session expired");
        }
      } catch {
        // Either currentSession threw (refresh failed, no user, etc.)
        // or the returned session isn't valid. Sign out locally and
        // navigate. logout() swallows its own errors; we don't
        // re-throw because there's no caller to handle it.
        await cognitologin.logout();
        navigate(LOGIN_PAGE);
      } finally {
        checkInFlightRef.current = false;
      }
    };

    // Initial probe on mount — catches sessions that died while
    // the tab was closed.
    probeSession();

    const intervalId = window.setInterval(probeSession, pollIntervalMs);

    const handleVisibilityChange = (): void => {
      if (document.visibilityState === "visible") {
        probeSession();
      }
    };
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      window.clearInterval(intervalId);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [cognitologin, navigate, pollIntervalMs]);
}
